import React, { useState } from 'react';
import { Send, ChevronDown, Trophy } from 'lucide-react';
import { api } from '../api';
import { errorMessage } from '../errors';
import { fmt$, bakeoffYearOptions, BAKEOFF_BUSINESS_RETURNS, BAKEOFF_K1_FIELDS, BAKEOFF_ROYALTY_FIELDS } from './constants';
import { Button } from '../../components/ui/button';
import { BakeoffSendModal } from './BakeoffSendModal';
import { BRAND } from '../kit/tokens';
import { toast } from '../../hooks/use-toast';
import PickSelect from '../shared/PickSelect';
import { NumIn, YearExtras } from './BakeoffInputs';

// ============================================================================
// AgencyComparison — quick Sch C inputs → side-by-side qualifying income
// across Fannie / Freddie / FHA / VA / USDA
// ============================================================================
const AGENCY_STYLE = {
  fannie:  { color: '#065F46', bg: '#ECFDF5', short: 'FNMA' },
  freddie: { color: '#1E40AF', bg: '#EFF6FF', short: 'FHLMC' },
  fha:     { color: '#9A3412', bg: '#FFF7ED', short: 'FHA' },
  va:      { color: '#7C2D12', bg: '#FEF3C7', short: 'VA' },
  usda:    { color: '#166534', bg: '#F0FDF4', short: 'USDA' },
};
const order = ['fannie', 'freddie', 'fha', 'va', 'usda'];

// Net profit is the one line a year cannot be compared without. The other
// lines are add-backs: left empty, they are not sent.
const REQUIRED = 'net_profit_loss';
const FIELDS = [
  { field: 'net_profit_loss', label: 'Net Profit (L31)' },
  { field: 'depreciation', label: 'Depreciation (L13)' },
  { field: 'meals_50pct_exclusion', label: 'Meals 50% (L24b)' },
  { field: 'business_use_home', label: 'Home Office (L30)' },
  { field: 'business_miles', label: 'Business Miles', miles: true },
  { field: 'depletion', label: 'Depletion (L12)' },
  { field: 'amortization_casualty', label: 'Amort/Cas' },
];

const emptyExtras = () => ({ k1s: [], biz: { type: 'none', values: {} } });
const entered = (v) => v != null;
const k1Entered = (k) => BAKEOFF_K1_FIELDS.some((f) => entered(k[f.field]));

export function AgencyComparison() {
  const [open, setOpen] = useState(false);
  const [years, setYears] = useState(1);
  // The most recent completed tax year by default; the prior year is always the one before it.
  const [yearOptions] = useState(() => bakeoffYearOptions());
  const [recentYear, setRecentYear] = useState(() => Number(yearOptions[0].value));
  const priorYear = recentYear - 1;
  const [inputs, setInputs] = useState({});
  // K-1s and the business return, per year: { recent, prior }
  const [extras, setExtrasState] = useState({ recent: emptyExtras(), prior: emptyExtras() });
  const [showExtras, setShowExtras] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [lastBody, setLastBody] = useState(null);

  // Anything that changes the inputs, the year, or the mode makes a shown result stale.
  const clearResult = () => {
    setResult(null);
    setLastBody(null);
  };
  const setInput = (k, n) => {
    setInputs((prev) => ({ ...prev, [k]: n }));
    clearResult();
  };
  const setExtras = (prefix) => (next) => {
    setExtrasState((prev) => ({ ...prev, [prefix]: next }));
    clearResult();
  };
  const pickYears = (n) => {
    if (n === years) return;
    setYears(n);
    clearResult();
  };

  const shownPrefixes = years === 2 ? ['recent', 'prior'] : ['recent'];
  // A year needs a Schedule C net profit, or another income line when there is no Schedule C.
  // Add-backs without a net profit are not a Schedule C, so they hold Compare back.
  const yearReady = (pfx) => {
    const netProfit = entered(inputs[`${pfx}_${REQUIRED}`]);
    const hasSchC = FIELDS.some((f) => entered(inputs[`${pfx}_${f.field}`]));
    if (hasSchC && !netProfit) return false;
    return netProfit
      || entered(inputs[`${pfx}_w2_self_employed_income`])
      || entered(inputs[`${pfx}_royalties_received`])
      || extras[pfx].k1s.some(k1Entered);
  };
  const missingRequired = !shownPrefixes.every(yearReady);
  const canCompare = !busy && !missingRequired;

  const buildComparePayload = () => {
    const buildYear = (prefix, tax_year) => {
      const year = { tax_year };
      if (entered(inputs[`${prefix}_${REQUIRED}`])) {
        const line = { business_name: 'Comparison', net_profit_loss: inputs[`${prefix}_${REQUIRED}`] };
        FIELDS.forEach(({ field }) => {
          const v = inputs[`${prefix}_${field}`];
          if (field !== REQUIRED && v != null) line[field] = v;
        });
        year.schedule_c = [line];
      }
      ['w2_self_employed_income', ...BAKEOFF_ROYALTY_FIELDS.map((f) => f.field)].forEach((field) => {
        const v = inputs[`${prefix}_${field}`];
        if (v != null) year[field] = v;
      });
      const k1s = extras[prefix].k1s.filter(k1Entered).map((k) => {
        const line = { entity_type: k.entity_type };
        BAKEOFF_K1_FIELDS.forEach(({ field, partnershipOnly }) => {
          if (k[field] != null && (!partnershipOnly || k.entity_type === 'partnership')) line[field] = k[field];
        });
        return line;
      });
      if (k1s.length) year.k1s = k1s;
      const { type, values } = extras[prefix].biz;
      const given = Object.entries(values).filter(([, v]) => v != null);
      if (BAKEOFF_BUSINESS_RETURNS[type] && given.length) {
        year[BAKEOFF_BUSINESS_RETURNS[type].key] = [Object.fromEntries(given)];
      }
      return year;
    };
    return {
      borrower_name: 'Agency Comparison',
      // Oldest year first: the backend reads the last entry as the most recent.
      years: years === 2
        ? [buildYear('prior', priorYear), buildYear('recent', recentYear)]
        : [buildYear('recent', recentYear)],
    };
  };

  const runCompare = async () => {
    if (!canCompare) return;
    setBusy(true);
    setResult(null);
    try {
      const body = buildComparePayload();
      setLastBody(body);
      const { data } = await api.post('/income/form-91/compare', body);
      setResult(data);
    } catch (e) {
      toast({ title: 'Comparison failed', description: errorMessage(e, 'The comparison could not be run.') });
    } finally {
      setBusy(false);
    }
  };

  const resultYears = (result?.tax_years || []).filter((y) => y != null).join(' and ');
  const pickRecent = (v) => {
    setRecentYear(Number(v));
    clearResult();
  };

  return (
    <div className="rounded-2xl border border-slate-200 shadow-sm bg-white overflow-hidden" data-testid="agency-comparison-card">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition"
        data-testid="toggle-agency-comparison"
      >
        <div className="text-left">
          <div className="text-[11px] uppercase tracking-widest font-bold text-brand-accent">Program Bakeoff</div>
          <div className="font-serif text-base font-semibold text-brand-navy">
            Compare qualifying income across all 5 agencies
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500 hidden md:inline">
            Fannie · Freddie · FHA · VA · USDA
          </span>
          <ChevronDown size={18} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <span className="text-xs font-semibold text-slate-600 pb-2">Tax Years:</span>
            <button
              onClick={() => pickYears(1)}
              className={`px-3 py-1 rounded-md text-xs font-semibold mb-1 ${years === 1 ? 'bg-brand-navy text-white' : 'bg-slate-100 text-slate-600'}`}
              data-testid="compare-years-1"
            >1 Year</button>
            <button
              onClick={() => pickYears(2)}
              className={`px-3 py-1 rounded-md text-xs font-semibold mb-1 ${years === 2 ? 'bg-brand-navy text-white' : 'bg-slate-100 text-slate-600'}`}
              data-testid="compare-years-2"
            >2 Years</button>
            <div className="w-28">
              <PickSelect
                label={years === 2 ? 'Most recent year' : 'Tax year'}
                value={String(recentYear)}
                onChange={pickRecent}
                options={yearOptions}
                testId="compare-year-recent"
              />
            </div>
          </div>

          {/* Year inputs: the recent year is always first; the prior year sits below it */}
          {shownPrefixes.map((pfx) => (
            <div key={pfx} className="rounded-lg border border-slate-200 p-3 bg-slate-50">
              <div className="text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-2" data-testid={`compare-heading-${pfx}`}>
                Schedule C for {pfx === 'recent' ? recentYear : priorYear}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {FIELDS.map((f) => (
                  <NumIn
                    key={f.field}
                    label={f.label}
                    miles={f.miles}
                    value={inputs[`${pfx}_${f.field}`]}
                    onChange={(n) => setInput(`${pfx}_${f.field}`, n)}
                    testId={`compare-${pfx}-${f.field}`}
                  />
                ))}
              </div>
              {showExtras && (
                <YearExtras
                  prefix={pfx}
                  inputs={inputs}
                  setInput={setInput}
                  extras={extras[pfx]}
                  setExtras={setExtras(pfx)}
                />
              )}
            </div>
          ))}
          {!showExtras && (
            <button
              type="button"
              onClick={() => setShowExtras(true)}
              className="text-xs font-semibold text-brand-navy hover:underline"
              data-testid="compare-more-toggle"
            >Add W-2, K-1, Schedule E royalties or business-return lines</button>
          )}

          <Button
            onClick={runCompare}
            disabled={!canCompare}
            variant="navy"
            className="w-full"
            data-testid="run-compare-btn"
          >
            {busy ? 'Running comparison…' : 'Compare across all 5 agencies'}
          </Button>
          {!busy && missingRequired && (
            <div className="text-xs text-slate-500" data-testid="compare-hint">
              Enter Net Profit (L31) for each year to compare, or another income line (W-2, royalties, K-1) if there is no Schedule C. Leave a line empty if the return shows nothing on it.
            </div>
          )}

          {/* Results table */}
          {result && (
            <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="comparison-results">
              {/* Winner banner */}
              <div className="px-4 py-3" style={{ background: 'linear-gradient(90deg, #ECFDF5 0%, #F0FDF4 100%)', borderBottom: '1px solid #D1FAE5' }}>
                <div className="flex items-center gap-2">
                  <Trophy size={16} style={{ color: '#059669' }} />
                  {result.winner ? (
                    <>
                      <span className="text-sm font-semibold" style={{ color: '#065F46' }}>
                        Best program: {result.winner.display_name}
                      </span>
                      <span className="ml-auto text-lg font-bold" style={{ color: '#059669' }}>
                        {fmt$(result.winner.qualifying_monthly_income)}/mo
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-sm font-semibold" style={{ color: '#B45309' }} data-testid="comparison-no-winner">
                        No program has a number: declining income, underwriter review
                      </span>
                      <span className="ml-auto text-lg font-bold" style={{ color: '#B45309' }}>—</span>
                    </>
                  )}
                </div>
                {resultYears && (
                  <div className="text-[11px] mt-1" style={{ color: '#065F46' }} data-testid="comparison-tax-years">
                    Tax {result.tax_years.length === 1 ? 'year' : 'years'}: {resultYears}
                  </div>
                )}
              </div>

              {/* Per-agency grid */}
              <div className="divide-y divide-slate-100">
                {order.map((key) => {
                  const r = result.agencies[key];
                  const s = AGENCY_STYLE[key];
                  return (
                    <div key={key} className="px-4 py-3 flex items-center gap-3" data-testid={`comparison-row-${key}`}>
                      <div className="w-16 text-center py-1 px-1 rounded"
                           style={{ background: s.bg, color: s.color, fontSize: 10, fontWeight: 700 }}>
                        {s.short}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-800 truncate">
                          {r.display_name}
                          {r.is_best && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded" style={{ background: '#DCFCE7', color: '#166534' }}>BEST</span>}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {r.method_note} • {r.handbook_reference}
                          {r.declining_income_detected && r.qualifying_monthly_income != null && <span style={{ color: '#B45309' }}> • Declining income adj.</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-base font-bold" style={{ color: r.is_best ? '#059669' : BRAND.navy }}>
                          {r.qualifying_monthly_income == null ? '—' : fmt$(r.qualifying_monthly_income)}
                        </div>
                        {!r.is_best && r.delta_from_max != null && (
                          <div className="text-[11px] font-semibold" style={{ color: '#B91C1C' }}>
                            {fmt$(r.delta_from_max)}/mo
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Divergence explanation */}
              {result.divergence_summary && result.divergence_summary.length > 0 && (
                <div className="px-4 py-3 bg-amber-50 border-t border-amber-200">
                  <div className="text-[11px] uppercase tracking-wide font-bold text-amber-800 mb-1">
                    Why do they differ?
                  </div>
                  <ul className="text-xs text-amber-900 space-y-1 list-disc pl-5">
                    {result.divergence_summary.map((d, i) => (
                      <li key={i}>
                        <b>{d.display_name}</b>: {fmt$(d.diff_vs_freddie_monthly)}/mo less than Freddie
                        {d.reasons.length > 0 && <> — {d.reasons.join('; ')}</>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Send to Borrower CTA */}
              <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                <div className="text-xs text-slate-600">
                  Turn this analysis into a client-ready PDF.
                </div>
                <Button
                  onClick={() => setSendOpen(true)}
                  data-testid="send-bakeoff-btn"
                  variant="navy"
                >
                  <Send size={14} />
                  Send Bakeoff to Borrower
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {sendOpen && lastBody && (
        <BakeoffSendModal
          basePayload={lastBody}
          winner={result?.winner}
          onClose={() => setSendOpen(false)}
        />
      )}
    </div>
  );
}
